import { useFormContext } from "react-hook-form";
import { TimeFieldProps } from "./Time.types";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { Input } from "@shadcn/components/ui/input";
import { Label } from "@shadcn/components/ui/label";
import { getValidationConfig } from "@forms/form-render/helpers/getValidationConfig";
import { getFieldHelperText } from "@forms/form-render/helpers/getFieldHelperText";
import InputMask from "react-input-mask";
import { Clock } from "lucide-react";
import { cn } from "@shadcn/lib/utils";
import { useIsMobile } from "@shadcn/hooks/use-is-mobile";

export const Time = ({ label, name, required, validation, config, disabled }: TimeFieldProps) => {
  const { control } = useFormContext();
  const isMobile = useIsMobile();
  const description = getFieldHelperText(config.helperText, required);
  const validationConfig = getValidationConfig(validation);
  const showSeconds = config.showSeconds || false;
  const mask = showSeconds ? "99:99:99" : "99:99";
  const placeholder = showSeconds ? "HH:MM:SS" : "HH:MM";

  return (
    <FormField
      control={control}
      defaultValue={config.defaultValue || ""}
      rules={{
        ...validationConfig,
        required: {
          value: required,
          message: "This is a required field",
        },
        pattern: {
          value: showSeconds ? /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/ : /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/,
          message: showSeconds ? "Time must be in HH:MM:SS format" : "Time must be in HH:MM format",
        },
      }}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-2">
          {label && <Label htmlFor={name}>{label}</Label>}
          <FormMessage />
          <FormControl>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <InputMask
                mask={mask}
                value={field.value || ""}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                onBlur={field.onBlur}
                disabled={disabled}
                maskChar={null}
              >
                {(inputProps: any) => (
                  <Input
                    {...inputProps}
                    id={name}
                    type="text"
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-label={label}
                    inputMode={isMobile ? "numeric" : "text"}
                    pattern={isMobile ? "[0-9]*" : undefined}
                    className={cn("pl-9", inputProps.className)}
                  />
                )}
              </InputMask>
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
        </FormItem>
      )}
    />
  );
};

